import { TRIGGER_TYPES, type DeskDetail, type DeskTrigger, type TriggerType } from '@deskworks/shared-types';
import { Plus, Timer, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { CopyButton } from '@/components/code-block';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { TRIGGER_TYPE } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

function webhookUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

function describeSchedule(trigger: DeskTrigger): string {
  if (trigger.type === 'cron') return t('Cron: {expression}', { expression: String(trigger.config.expression ?? '—') });
  if (trigger.type === 'polling') {
    const s = Number(trigger.config.intervalSeconds ?? 0);
    if (!s) return t('Interval not set');
    if (s % 3600 === 0) return t('Every {n} hr', { n: s / 3600 });
    if (s % 60 === 0) return t('Every {n} min', { n: s / 60 });
    return t('Every {n} sec', { n: s });
  }
  if (trigger.type === 'webhook') return t('Woken up from outside via Webhook');
  return t('Only when you click the button');
}

export function DeskTriggersSection({ desk }: { desk: DeskDetail }) {
  const invalidate = [qk.desk(desk.id)];
  const toggle = useApiMutation((trigger: DeskTrigger) => api.triggers.update(trigger.id, { enabled: !trigger.enabled }), {
    invalidate,
    success: (trigger) => (trigger.enabled ? t('Trigger enabled') : t('Trigger disabled')),
  });
  const remove = useApiMutation((trigger: DeskTrigger) => api.triggers.delete(trigger.id), { invalidate, success: t('Trigger deleted') });

  return (
    <Section
      icon={<Timer />}
      title={t('Triggers')}
      description={t('Triggers wake up the desk. The Lead Agent then checks whether there is anything to do.')}
      actions={
        <AddTriggerDialog deskId={desk.id}>
          <Button variant="outline" size="sm">
            <Plus /> Trigger
          </Button>
        </AddTriggerDialog>
      }
    >
      {desk.triggers.length === 0 ? (
        <EmptyState
          compact
          title={t('No Triggers yet')}
          description={t('Without a Trigger, the desk only becomes active when you click “Run now”. Add a schedule, for example.')}
        />
      ) : (
        <ul className="-mx-5 -mb-5 divide-y border-t">
          {desk.triggers.map((trigger) => (
            <li key={trigger.id} className="flex flex-col gap-2 px-5 py-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={trigger.enabled}
                  onCheckedChange={() => toggle.mutate(trigger)}
                  disabled={toggle.isPending}
                  aria-label={trigger.enabled ? t('Disable') : t('Enable')}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{trigger.name}</span>
                    <Badge tone="outline">{TRIGGER_TYPE[trigger.type]?.label ?? trigger.type}</Badge>
                    {!trigger.enabled && <Badge tone="muted">{t('Off')}</Badge>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    <span>{describeSchedule(trigger)}</span>
                    <span>
                      {tr('Last: {time}', { time: <RelativeTime value={trigger.lastFiredAt} fallback={t('never')} /> })}
                    </span>
                    {trigger.enabled && trigger.nextFireAt && (
                      <span>
                        {tr('Next: {time}', { time: <RelativeTime value={trigger.nextFireAt} /> })}
                      </span>
                    )}
                  </div>
                </div>
                <Confirm
                  title={t('Delete Trigger “{name}”?', { name: trigger.name })}
                  description={t('The desk will no longer be woken up this way.')}
                  confirmLabel={t('Delete')}
                  destructive
                  onConfirm={() => remove.mutateAsync(trigger)}
                >
                  <Button variant="ghost" size="icon-sm" aria-label={t('Delete Trigger')}>
                    <Trash2 />
                  </Button>
                </Confirm>
              </div>
              {trigger.type === 'webhook' && trigger.webhookPath && (
                <div className="ml-12 flex items-center gap-1 rounded-md border bg-muted/40 py-1 pr-1 pl-2.5">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs" title={webhookUrl(trigger.webhookPath)}>
                    POST {webhookUrl(trigger.webhookPath)}
                  </code>
                  <CopyButton value={webhookUrl(trigger.webhookPath)} label={t('Copy URL')} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function AddTriggerDialog({ deskId, children }: { deskId: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<TriggerType>('cron');
  const [name, setName] = useState('');
  const [expression, setExpression] = useState('0 * * * *');
  const [minutes, setMinutes] = useState('60');
  const [enabled, setEnabled] = useState(true);

  const create = useApiMutation(
    () => {
      const config: Record<string, unknown> =
        type === 'cron'
          ? { expression: expression.trim() }
          : type === 'polling'
            ? { intervalSeconds: Math.max(1, Math.round(Number(minutes) * 60)) }
            : {};
      return api.triggers.create(deskId, { type, name: name.trim() || TRIGGER_TYPE[type].label, config, enabled });
    },
    {
      success: t('Trigger created'),
      invalidate: [qk.desk(deskId)],
      onSuccess: () => {
        setOpen(false);
        setName('');
      },
    },
  );

  const invalid = (type === 'cron' && !expression.trim()) || (type === 'polling' && !(Number(minutes) > 0));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!invalid) create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Add Trigger')}</DialogTitle>
            <DialogDescription>{t('When should the desk wake up and look for work?')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Type')} hint={TRIGGER_TYPE[type].description}>
            <Select value={type} onValueChange={(v) => setType(v as TriggerType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGER_TYPES.map((tt) => (
                  <SelectItem key={tt} value={tt}>
                    {TRIGGER_TYPE[tt].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('Name')} optional>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={TRIGGER_TYPE[type].label} />
          </Field>
          {type === 'cron' && (
            <Field
              label={t('Cron expression')}
              hint={tr('Examples: {hourly} = every hour on the hour, {weekdays} = weekdays at 8 am.', {
                hourly: <code className="font-mono">0 * * * *</code>,
                weekdays: <code className="font-mono">0 8 * * 1-5</code>,
              })}
            >
              <Input value={expression} onChange={(e) => setExpression(e.target.value)} className="font-mono" />
            </Field>
          )}
          {type === 'polling' && (
            <Field label={t('Interval in minutes')} hint={t('How often should the Lead Agent check for new work?')}>
              <Input type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </Field>
          )}
          {type === 'webhook' && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t('After you create it, you get a secret URL. An external system can call it via POST to wake up the desk.')}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Switch id="trigger-enabled" checked={enabled} onCheckedChange={setEnabled} />
            <Label htmlFor="trigger-enabled">{t('Active right away')}</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={invalid}>
              {t('Add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
